'use client';
import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { MyMembershipPlan, PlanStatus } from '@/lib/membership/useMyMembershipPlan';
import type { LockReason as JourneyLockReason } from '@/app/implementation-tracker/components/useJourneyLock';

export interface AppJourney {
  unlockedThrough: number;
  course1Completed: boolean;
  hasPaidMembership: boolean;
  lockReason: JourneyLockReason;
}

export interface SessionSnapshot {
  /** false until the first check has finished */
  ready: boolean;
  signedIn: boolean;
  userId: string | null;
  email: string | null;
  fullName: string;
  firstName: string;
  role: string | null;
  mustSetPassword: boolean;
  isAdmin: boolean;
  isMentorOrAdmin: boolean;
  isCrmStaff: boolean;
  isCrmSuperuser: boolean;
  plan: MyMembershipPlan | null;
  journey: AppJourney | null;
  unreadMessages: number;
  canUseMessaging: boolean;
  canUseProgressTracking: boolean;
  // Per-visit caches that the sidebar fills in once (not part of member_app_context).
  hasVideos: boolean | null;
  badges: { enquiries: number; pending: number; cancellations: number };
  lastBadgeFetch: number;
}

const EMPTY: SessionSnapshot = {
  ready: false,
  signedIn: false,
  userId: null,
  email: null,
  fullName: '',
  firstName: '',
  role: null,
  mustSetPassword: false,
  isAdmin: false,
  isMentorOrAdmin: false,
  isCrmStaff: false,
  isCrmSuperuser: false,
  plan: null,
  journey: null,
  unreadMessages: 0,
  canUseMessaging: false,
  canUseProgressTracking: false,
  hasVideos: null,
  badges: { enquiries: 0, pending: 0, cancellations: 0 },
  lastBadgeFetch: 0,
};

const KNOWN_STATUSES: PlanStatus[] = ['active', 'paid_in_full', 'ending', 'payment_overdue', 'expired', 'not_assigned'];
const POLL_MS = 60000;

let snapshot: SessionSnapshot = { ...EMPTY, badges: { ...EMPTY.badges } };
let inflight: Promise<SessionSnapshot> | null = null;
let started = false;
const listeners = new Set<() => void>();

function publish(next: SessionSnapshot) {
  snapshot = next;
  listeners.forEach((l) => l());
}

function signedOut(): SessionSnapshot {
  return { ...EMPTY, ready: true, badges: { enquiries: 0, pending: 0, cancellations: 0 } };
}

function mapPlan(p: any): MyMembershipPlan | null {
  if (!p) return null;
  const raw = String(p.status ?? 'not_assigned');
  const status = (KNOWN_STATUSES.includes(raw as PlanStatus) ? raw : 'not_assigned') as PlanStatus;
  const days = p.days_remaining;
  return {
    route: p.route ?? null,
    planName: p.plan_name ?? null,
    planDescription: p.plan_description ?? null,
    startDate: p.start_date ?? null,
    expiryDate: p.expiry_date ?? null,
    daysRemaining: days === null || days === undefined ? null : Number(days),
    status,
    isStaff: p.is_staff === true,
  };
}

function mapJourney(j: any): AppJourney | null {
  if (!j) return null;
  return {
    unlockedThrough: Number(j.unlocked_through) >= 12 ? 12 : 1,
    course1Completed: j.course1_completed === true,
    hasPaidMembership: j.has_paid_membership === true,
    lockReason: (j.lock_reason ?? 'unlocked') as JourneyLockReason,
  };
}

function mapContext(d: any, prev: SessionSnapshot): SessionSnapshot {
  if (!d || d.logged_in !== true) return signedOut();
  const roles = d.roles ?? {};
  return {
    ...prev,
    ready: true,
    signedIn: true,
    userId: d.user_id ?? null,
    email: d.email ?? null,
    fullName: d.full_name ?? '',
    firstName: d.first_name ?? '',
    role: d.role ?? null,
    mustSetPassword: d.must_set_password === true,
    isAdmin: roles.is_admin === true,
    isMentorOrAdmin: roles.is_mentor_or_admin === true,
    isCrmStaff: roles.is_crm_staff === true,
    isCrmSuperuser: roles.is_crm_superuser === true,
    plan: mapPlan(d.plan),
    journey: mapJourney(d.journey),
    unreadMessages: Number(d.unread_messages) || 0,
    canUseMessaging: d.can_use_messaging === true,
    canUseProgressTracking: d.can_use_progress_tracking === true,
  };
}

async function runCheck(): Promise<SessionSnapshot> {
  const supabase = createClient();
  try {
    // getSession reads local storage/cookies: no network request.
    const { data: sess } = await supabase.auth.getSession();
    if (!sess?.session) {
      const out = signedOut();
      publish(out);
      return out;
    }
    const { data, error } = await supabase.rpc('member_app_context');
    if (error || !data) {
      // Keep the user signed in with no elevated roles if the bundle fails.
      const out: SessionSnapshot = {
        ...snapshot,
        ready: true,
        signedIn: true,
        userId: sess.session.user.id,
        email: sess.session.user.email ?? null,
        mustSetPassword: sess.session.user.user_metadata?.must_set_password === true,
      };
      publish(out);
      return out;
    }
    const row = Array.isArray(data) ? data[0] : data;
    const out = mapContext(row, snapshot);
    publish(out);
    return out;
  } catch {
    const out = signedOut();
    publish(out);
    return out;
  }
}

/** Loads member_app_context once and shares the result. Pass force=true to refresh. */
export function ensureSession(force = false): Promise<SessionSnapshot> {
  if (!force && snapshot.ready) return Promise.resolve(snapshot);
  if (inflight) return inflight;
  inflight = runCheck().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Re-fetch the bundle (after saving a password, sending/reading messages, etc.). */
export function refreshAppContext(): Promise<SessionSnapshot> {
  return ensureSession(true);
}

/** Patch per-visit caches (videos flag, badge counts) without a refetch. */
export function patchSessionCache(patch: Partial<SessionSnapshot>) {
  // Mutate in place so references held by the sidebar (e.g. badges) stay valid.
  Object.assign(snapshot, patch);
  // no publish: these are read on mount by the sidebar only
}

let pollTimer: ReturnType<typeof setInterval> | null = null;

function stopPoll() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function startPoll() {
  if (pollTimer || document.visibilityState !== 'visible') return;
  pollTimer = setInterval(() => {
    if (snapshot.signedIn && document.visibilityState === 'visible') void ensureSession(true);
  }, POLL_MS);
}

function start() {
  if (started || typeof window === 'undefined') return;
  started = true;
  const supabase = createClient();
  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT') {
      publish(signedOut());
      stopPoll();
    } else if (event === 'SIGNED_IN' || event === 'USER_UPDATED') {
      // Defer so we never call supabase inside the auth callback itself.
      setTimeout(() => {
        void ensureSession(true).then(() => startPoll());
      }, 0);
    }
    // TOKEN_REFRESHED does not change roles, so it never triggers a refetch.
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      startPoll();
    } else {
      stopPoll();
    }
  });
  void ensureSession().then(() => startPoll());
}

export function startAppContext() {
  start();
}

export function getSessionSnapshot(): SessionSnapshot {
  return snapshot;
}

export function useSessionCache(): SessionSnapshot {
  const [, force] = useState(0);
  useEffect(() => {
    start();
    const l = () => force((n) => n + 1);
    listeners.add(l);
    // pick up anything published between render and effect
    l();
    return () => {
      listeners.delete(l);
    };
  }, []);
  return snapshot;
}

/** Alias with the name used in the app-context design. */
export const useAppContext = useSessionCache;
